import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Bell, Home, Users, Settings, TrendingUp, ChevronRight,
  Loader2, Eye, BedDouble, Bath, MapPin, DollarSign, Clock
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PropertyAlertsPage() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [similarProperties, setSimilarProperties] = useState([]);
  const [newAllyProperties, setNewAllyProperties] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [activeTab, setActiveTab] = useState('similar');
  
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [summaryRes, similarRes, newRes, prefsRes] = await Promise.all([
        axios.get(`${API_URL}/api/alerts/summary`, { headers }),
        axios.get(`${API_URL}/api/alerts/similar-from-allies`, { headers }),
        axios.get(`${API_URL}/api/alerts/new-ally-properties?days=7`, { headers }),
        axios.get(`${API_URL}/api/alerts/preferences`, { headers })
      ]);
      
      setSummary(summaryRes.data.summary || {});
      setSimilarProperties(similarRes.data.alerts || []);
      setNewAllyProperties(newRes.data.properties || []);
      setPreferences(prefsRes.data.preferences || {});
    } catch (err) {
      console.error('Error loading alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return `$${price.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Bell className="w-8 h-8 text-indigo-600" />
                Alertas de Propiedades
              </h1>
              <p className="text-gray-500 mt-1">
                Propiedades de tus aliados que podrían interesarte
              </p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{summary.active_allies || 0}</p>
                  <p className="text-sm text-gray-500">Aliados activos</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Home className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.new_ally_properties_this_week || 0}
                  </p>
                  <p className="text-sm text-gray-500">Nuevas esta semana</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.pending_alliance_requests || 0}
                  </p>
                  <p className="text-sm text-gray-500">Solicitudes pendientes</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{similarProperties.length}</p>
                  <p className="text-sm text-gray-500">Propiedades similares</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {[
              { id: 'similar', name: 'Similares a las tuyas', count: similarProperties.length },
              { id: 'new', name: 'Nuevas de aliados', count: newAllyProperties.length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.name}
                {tab.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          {activeTab === 'similar' && (
            <div>
              {similarProperties.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                  <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Sin propiedades similares
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    Agrega propiedades y forma alianzas para ver propiedades similares 
                    de tus colegas que podrías ofrecer a tus clientes
                  </p>
                  <Link
                    to="/crm/alianzas"
                    className="inline-flex items-center gap-2 mt-4 text-indigo-600 hover:underline"
                  >
                    Buscar aliados <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {similarProperties.map(prop => (
                    <PropertyAlertCard key={prop.property_id} property={prop} formatPrice={formatPrice} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'new' && (
            <div>
              {newAllyProperties.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                  <Home className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Sin propiedades nuevas
                  </h3>
                  <p className="text-gray-500">
                    Tus aliados no han agregado propiedades en los últimos 7 días
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {newAllyProperties.map(prop => (
                    <PropertyCard key={prop.id} property={prop} formatPrice={formatPrice} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Info Banner */}
          <div className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-start gap-4">
              <Bell className="w-10 h-10 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold mb-2">Sistema de Alertas Inteligente</h3>
                <p className="text-indigo-100 text-sm leading-relaxed">
                  Las alertas te muestran propiedades de tus aliados que son similares a las que 
                  tú manejas. Esto te permite ofrecer más opciones a tus clientes sin perder 
                  oportunidades cuando no tienes una propiedad que se ajuste a sus necesidades.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function PropertyAlertCard({ property, formatPrice }) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="relative h-40">
        {property.images?.[0] ? (
          <img 
            src={typeof property.images[0] === 'string' ? property.images[0] : property.images[0].url}
            alt={property.property_title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <Home className="w-10 h-10 text-gray-400" />
          </div>
        )}
        
        {/* Similarity badge */}
        <div className="absolute top-2 right-2 px-2 py-1 bg-indigo-600 text-white rounded-full text-xs font-medium">
          {property.similarity_score}% similar
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
            Aliado: {property.ally_name}
          </span>
        </div>
        
        <h3 className="font-semibold text-gray-900 line-clamp-1">{property.property_title}</h3>
        
        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
          <MapPin className="w-3 h-3" />
          {property.city}
        </div>
        
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
          {property.bedrooms > 0 && (
            <span className="flex items-center gap-1">
              <BedDouble className="w-4 h-4" />
              {property.bedrooms}
            </span>
          )}
          {property.bathrooms > 0 && (
            <span className="flex items-center gap-1">
              <Bath className="w-4 h-4" />
              {property.bathrooms}
            </span>
          )}
          {property.construction_area > 0 && (
            <span>{property.construction_area} m²</span>
          )}
        </div>
        
        {/* Reasons */}
        <div className="mt-3 flex flex-wrap gap-1">
          {property.reasons?.slice(0, 2).map((reason, i) => (
            <span key={i} className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
              {reason}
            </span>
          ))}
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <span className="text-lg font-bold text-indigo-600">
            {formatPrice(property.price)}
          </span>
          <a
            href={`${API_URL}/api/fichas/property/${property.property_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
          >
            Ver ficha <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

function PropertyCard({ property, formatPrice }) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="relative h-40">
        {property.images?.[0] ? (
          <img 
            src={typeof property.images[0] === 'string' ? property.images[0] : property.images[0].url}
            alt={property.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <Home className="w-10 h-10 text-gray-400" />
          </div>
        )}
        
        <div className="absolute top-2 left-2 px-2 py-1 bg-green-500 text-white rounded-full text-xs">
          Nueva
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
            {property.ally_name}
          </span>
        </div>
        
        <h3 className="font-semibold text-gray-900 line-clamp-1">{property.title}</h3>
        
        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
          <MapPin className="w-3 h-3" />
          {property.city}, {property.colony}
        </div>
        
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
          {property.bedrooms > 0 && (
            <span className="flex items-center gap-1">
              <BedDouble className="w-4 h-4" />
              {property.bedrooms}
            </span>
          )}
          {property.bathrooms > 0 && (
            <span className="flex items-center gap-1">
              <Bath className="w-4 h-4" />
              {property.bathrooms}
            </span>
          )}
          {property.construction_area > 0 && (
            <span>{property.construction_area} m²</span>
          )}
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <span className="text-lg font-bold text-indigo-600">
            {formatPrice(property.price)}
          </span>
          <a
            href={`${API_URL}/api/fichas/property/${property.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
          >
            Ver ficha <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
